/**
 * The chat of a case as editable lines: a speaker and a text box per message, with move and
 * remove buttons. Tool items are kept as they are and shown read-only.
 */
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import Button from '../../../../components/Button';
import { FieldLabel, fieldClass, isRecord } from './editorParts';

export type Line =
  | { key: number; kind: 'message'; role: 'user' | 'assistant'; content: string; original?: Record<string, unknown> }
  | { key: number; kind: 'tool'; item: Record<string, unknown> };

let nextKey = 1;

/** The lines of `input.conversation`. */
export function toLines(input: unknown): Line[] {
  const conversation = isRecord(input) && Array.isArray(input.conversation) ? input.conversation.filter(isRecord) : [];
  return conversation.map((item): Line =>
    item.type === 'message'
      ? { key: nextKey++, kind: 'message', role: item.role === 'assistant' ? 'assistant' : 'user', content: typeof item.content === 'string' ? item.content : '', original: item }
      : { key: nextKey++, kind: 'tool', item },
  );
}

/** Message IDs follow the position, as the cases from code had them, so an unchanged chat keeps its hash. */
export function toConversation(lines: Line[]): Record<string, unknown>[] {
  return lines.map((line, index) =>
    line.kind === 'tool'
      ? line.item
      : { ...line.original, id: `msg_${index + 1}`, type: 'message', role: line.role, content: line.content, isHidden: line.original?.isHidden ?? false },
  );
}

const toolText = (item: Record<string, unknown>): string =>
  item.type === 'tool_call' ? `tool call: ${String(item.name)}(${String(item.arguments)})` : `tool result: ${String(item.tool_name)} → ${String(item.result)}`;

interface ConversationEditorProps {
  lines: Line[];
  hint: string;
  disabled: boolean;
  onChange: (lines: Line[]) => void;
}

export const ConversationEditor = ({ lines, hint, disabled, onChange }: ConversationEditorProps) => {
  const setLine = (index: number, line: Line) => onChange(lines.map((existing, at) => (at === index ? line : existing)));
  const move = (index: number, by: -1 | 1) => {
    const next = [...lines];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    onChange(next);
  };
  const addMessage = () => {
    const last = [...lines].reverse().find((line) => line.kind === 'message');
    const role = last?.kind === 'message' && last.role === 'user' ? 'assistant' : 'user';
    onChange([...lines, { key: nextKey++, kind: 'message', role, content: '' }]);
  };

  return (
    <div className="space-y-2">
      <FieldLabel title="Conversation" hint={hint} />
      {lines.map((line, index) => (
        <div key={line.key} className="flex items-start gap-2">
          {line.kind === 'message' ? (
            <>
              <select
                value={line.role}
                disabled={disabled}
                onChange={(event) => setLine(index, { ...line, role: event.target.value === 'assistant' ? 'assistant' : 'user' })}
                className="px-2 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 w-20 shrink-0"
              >
                <option value="user">User</option>
                <option value="assistant">Ollie</option>
              </select>
              <textarea
                value={line.content}
                disabled={disabled}
                rows={Math.min(8, Math.max(2, Math.ceil(line.content.length / 90)))}
                onChange={(event) => setLine(index, { ...line, content: event.target.value })}
                className={`${fieldClass} ${line.role === 'user' ? 'bg-blue-50' : 'bg-gray-50'}`}
              />
            </>
          ) : (
            <span className="flex-1 text-xs text-gray-600 font-mono py-2 pl-[5.5rem]">{toolText(line.item)}</span>
          )}
          <div className="flex flex-col shrink-0">
            <button type="button" disabled={disabled || index === 0} onClick={() => move(index, -1)} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30" aria-label="Move up">
              <ArrowUp size={14} />
            </button>
            <button
              type="button"
              disabled={disabled || index === lines.length - 1}
              onClick={() => move(index, 1)}
              className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
              aria-label="Move down"
            >
              <ArrowDown size={14} />
            </button>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(lines.filter((_, at) => at !== index))}
            className="p-1 mt-1 text-gray-400 hover:text-red-700 disabled:opacity-30"
            aria-label="Remove"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <Button variant="outline" size="sm" icon={<Plus size={13} />} onClick={addMessage} disabled={disabled}>
        Add message
      </Button>
    </div>
  );
};
